/**
 * Round structure: how many cards are dealt each round and who deals.
 */

import type { PlannedRound, RoundPlanConfig } from '@shared/types/game';

export const DECK_SIZE = 52;
export const MIN_PLAYERS = 3;
export const MAX_PLAYERS = 7;

/**
 * Default cap on the biggest round. The deck allows 13 cards with 4 players,
 * which makes a 25-round game — far too long with kids at a campsite table, so
 * the default stops at 7 and the user can raise it.
 */
export const DEFAULT_MAX_CARDS_CAP = 7;

/**
 * Largest number of cards that can be dealt to every player, honouring both
 * the deck and the user's cap.
 */
export function maxCardsFor(playerCount: number, cap: number = DEFAULT_MAX_CARDS_CAP): number {
  assertPlayerCount(playerCount);
  if (!Number.isInteger(cap) || cap < 1) {
    throw new RangeError(`cap must be a positive integer, got ${cap}`);
  }
  return Math.min(cap, Math.floor(DECK_SIZE / playerCount));
}

/** Deck limit alone, ignoring any user cap. */
export function deckLimitFor(playerCount: number): number {
  assertPlayerCount(playerCount);
  return Math.floor(DECK_SIZE / playerCount);
}

export function assertPlayerCount(playerCount: number): void {
  if (!Number.isInteger(playerCount) || playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
    throw new RangeError(
      `playerCount must be an integer between ${MIN_PLAYERS} and ${MAX_PLAYERS}, got ${playerCount}`
    );
  }
}

export interface LadderStep {
  cards: number;
  /** Played without trump on purpose, not because the deck ran out. */
  noTrump: boolean;
}

/**
 * The ladder of rounds, e.g. 1→7→1 for 'up-down' with a cap of 7.
 *
 * 'up-down' plays the biggest round once by default; `repeatTopRound` plays it
 * twice, which is the other common house convention.
 *
 * With `extraNoTrumpRound` one more round at the biggest hand size is inserted
 * right after the last of them, played without trump — so
 * 1 2 3 4 5 6 7 7* 6 5 4 3 2 1.
 *
 * With `noTrumpOnTheWayDown` the whole descent loses its trump as well, giving
 * 1 2 3 4 5 6 7 7* 6* 5* 4* 3* 2* 1*.
 */
export function roundLadder(config: RoundPlanConfig): LadderStep[] {
  const top = maxCardsFor(config.playerCount, config.maxCards);
  const up: number[] = [];
  for (let c = 1; c <= top; c++) up.push(c);

  let sizes: number[];
  switch (config.direction) {
    case 'up':
      sizes = up;
      break;
    case 'down':
      sizes = [...up].reverse();
      break;
    case 'up-down': {
      const down = [...up].reverse();
      // Drop the repeated top card unless the house rule keeps it.
      sizes = [...up, ...(config.repeatTopRound ? down : down.slice(1))];
      break;
    }
    default: {
      const exhaustive: never = config.direction;
      throw new Error(`unknown round direction: ${String(exhaustive)}`);
    }
  }

  const steps: LadderStep[] = sizes.map((cards) => ({ cards, noTrump: false }));

  // Must be opted into explicitly. A plan stored before this option existed has
  // it undefined, and its `rounds` array was built without the extra round — so
  // treating undefined as "on" would give that game a 14-round plan against 13
  // played rounds, and the "who can still win" arithmetic would drift.
  // New games set it to true; see DEFAULT_NEW_GAME and the room schema.
  if (config.extraNoTrumpRound === true) {
    const lastTop = steps.map((step) => step.cards).lastIndexOf(top);
    if (lastTop !== -1) {
      steps.splice(lastTop + 1, 0, { cards: top, noTrump: true });
    }
  }

  /*
   * Up with trump, down without.
   *
   * Only meaningful on 'up-down': in a one-way ladder every round is either on
   * the way up or the way down, so this would silently turn the whole game
   * trumpless. Computed after the insert above, so the extra top round counts as
   * the last of the top rounds and the descent starts below it.
   */
  if (config.noTrumpOnTheWayDown === true && config.direction === 'up-down') {
    const lastTop = steps.map((step) => step.cards).lastIndexOf(top);
    for (let i = lastTop + 1; i < steps.length; i++) {
      steps[i].noTrump = true;
    }
  }

  return steps;
}

/** Just the hand sizes, for previews and assertions. */
export function cardLadder(config: RoundPlanConfig): number[] {
  return roundLadder(config).map((step) => step.cards);
}

/**
 * The full plan for a game: one entry per round, with the dealer rotating one
 * seat per round starting from `firstDealerSeat`.
 */
export function buildRoundPlan(config: RoundPlanConfig): PlannedRound[] {
  assertPlayerCount(config.playerCount);
  if (
    !Number.isInteger(config.firstDealerSeat) ||
    config.firstDealerSeat < 0 ||
    config.firstDealerSeat >= config.playerCount
  ) {
    throw new RangeError(
      `firstDealerSeat must be a seat index in 0..${config.playerCount - 1}, got ${config.firstDealerSeat}`
    );
  }

  return roundLadder(config).map((step, index) => ({
    roundNo: index + 1,
    cards: step.cards,
    noTrump: step.noTrump,
    dealerSeat: (config.firstDealerSeat + index) % config.playerCount,
  }));
}

/** Seat that leads the first trick: the one to the dealer's left. */
export function leaderSeatFor(dealerSeat: number, playerCount: number): number {
  assertPlayerCount(playerCount);
  return (dealerSeat + 1) % playerCount;
}

/**
 * Bidding order for a round: starts left of the dealer and ends on the dealer,
 * who therefore bids last and carries the restriction.
 */
export function biddingOrderFor(dealerSeat: number, playerCount: number): number[] {
  assertPlayerCount(playerCount);
  const order: number[] = [];
  for (let i = 1; i <= playerCount; i++) {
    order.push((dealerSeat + i) % playerCount);
  }
  return order;
}

/** Total tricks played across the whole game. */
export function totalTricks(plan: readonly PlannedRound[]): number {
  return plan.reduce((sum, round) => sum + round.cards, 0);
}

const SECONDS_PER_TRICK = 25;
const SECONDS_PER_ROUND_OVERHEAD = 45;

/**
 * Rough playing time in minutes, shown on the new-game screen so nobody starts
 * a 25-round marathon by accident. Deliberately approximate.
 */
export function estimateMinutes(plan: readonly PlannedRound[]): number {
  const seconds = totalTricks(plan) * SECONDS_PER_TRICK + plan.length * SECONDS_PER_ROUND_OVERHEAD;
  return Math.round(seconds / 60);
}
